import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { I18NService } from '@core';
import { ALAIN_I18N_TOKEN, I18nPipe } from '@delon/theme';
import { NzAvatarModule } from 'ng-zorro-antd/avatar';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NZ_MODAL_DATA, NzModalRef } from 'ng-zorro-antd/modal';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzSwitchModule } from 'ng-zorro-antd/switch';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { NzUploadModule } from 'ng-zorro-antd/upload';
import { finalize } from 'rxjs';
import { HttpService } from 'src/app/http/http.service';
import { AvatarUploadModalComponent } from 'src/app/routes/avatar-modal/avatar-upload-modal.component';
import { ImageService } from 'src/app/services/image.service';
import { TestIdDirective } from 'src/app/shared/directives/test-id.directive';
import { ConstantUtil } from 'src/app/util/constant.util';

@Component({
  selector: 'identity-detail',
  templateUrl: './identity-detail.component.html',
  styleUrls: ['./identity-detail.component.less'],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    NzCardModule,
    NzFormModule,
    NzInputModule,
    NzSelectModule,
    NzSwitchModule,
    NzButtonModule,
    NzUploadModule,
    NzIconModule,
    NzAvatarModule,
    NzSpinModule,
    NzDividerModule,
    AvatarUploadModalComponent,
    I18nPipe,
    NzToolTipModule,
    TestIdDirective
  ],
  standalone: true
})
export class IdentityDetailComponent implements OnInit {
  loading = false;
  submitting = false;
  isAdd = true;
  identityId: string | null = null;
  serviceNumberId: string | null = null;
  identityForm: FormGroup;
  tempAvatarFile: any;

  // 身份类型选项
  typeOptions: Array<{ label: string; value: string }> = [];

  // 头像上传相关
  avatarUrl?: string;
  loading_avatar = false;

  // 添加属性
  isAvatarModalVisible = false;
  isAvatarUploading = false;

  private readonly i18n = inject<I18NService>(ALAIN_I18N_TOKEN);

  // TestId properties for template binding
  'identity-detail-container' = 'identity-detail-container';
  'identity-detail-loading' = 'identity-detail-loading';
  'identity-detail-content' = 'identity-detail-content';
  'identity-detail-form' = 'identity-detail-form';
  'identity-detail-card' = 'identity-detail-card';
  'identity-detail-card-header' = 'identity-detail-card-header';
  'identity-detail-title' = 'identity-detail-title';
  'identity-detail-divider' = 'identity-detail-divider';
  'identity-detail-form-row' = 'identity-detail-form-row';
  'identity-detail-form-col' = 'identity-detail-form-col';
  'identity-name-form-item' = 'identity-name-form-item';
  'identity-name-label' = 'identity-name-label';
  'identity-name-control' = 'identity-name-control';
  'identity-name-input' = 'identity-name-input';
  'identity-type-form-item' = 'identity-type-form-item';
  'identity-type-label' = 'identity-type-label';
  'identity-type-control' = 'identity-type-control';
  'identity-type-select' = 'identity-type-select';
  'identity-type-option' = 'identity-type-option';
  'identity-avatar-form-item' = 'identity-avatar-form-item';
  'identity-avatar-label' = 'identity-avatar-label';
  'identity-avatar-control' = 'identity-avatar-control';
  'identity-avatar-upload-container' = 'identity-avatar-upload-container';
  'identity-avatar-upload-wrapper' = 'identity-avatar-upload-wrapper';
  'identity-avatar-placeholder' = 'identity-avatar-placeholder';
  'identity-avatar-placeholder-icon' = 'identity-avatar-placeholder-icon';
  'identity-avatar-placeholder-text' = 'identity-avatar-placeholder-text';
  'identity-detail-avatar-image' = 'identity-detail-avatar-image';
  'identity-avatar-upload-button' = 'identity-avatar-upload-button';
  'identity-avatar-upload-icon' = 'identity-avatar-upload-icon';
  'identity-default-form-item' = 'identity-default-form-item';
  'identity-default-label' = 'identity-default-label';
  'identity-default-control' = 'identity-default-control';
  'identity-default-switch' = 'identity-default-switch';
  'identity-detail-actions' = 'identity-detail-actions';
  'identity-cancel-button' = 'identity-cancel-button';
  'identity-save-button' = 'identity-save-button';
  'identity-avatar-modal' = 'identity-avatar-modal';

  constructor(
    private readonly fb: FormBuilder,
    private readonly message: NzMessageService,
    private readonly http: HttpService,
    private readonly cdr: ChangeDetectorRef,
    private readonly imageService: ImageService
  ) {
    this.identityForm = this.fb.group({
      name: [null, [Validators.required]],
      type: ['Normal', [Validators.required]],
      isDefault: [false],
      avatarId: [null, [Validators.required]]
    });
  }

  private readonly modalRef = inject(NzModalRef, { optional: true });
  private readonly nzModalData = inject(NZ_MODAL_DATA, { optional: true }) || {};

  ngOnInit(): void {
    this.typeOptions = [
      { label: this.i18n.fanyi('servicenumber.identity.type.assistant'), value: 'Assistant' },
      { label: this.i18n.fanyi('servicenumber.identity.type.boss'), value: 'Boss' }
    ];
    // 从模态窗口数据中获取参数
    if (this.nzModalData) {
      this.serviceNumberId = this.nzModalData.serviceNumberId || null;
      this.identityId = this.nzModalData.identityId || null;
      this.isAdd = this.nzModalData.isAdd !== undefined ? this.nzModalData.isAdd : true;
    }

    if (!this.isAdd && this.identityId) {
      this.loadIdentityData(this.identityId);
    }
  }

  // 修改保存方法，支持模态窗口关闭
  saveIdentity(): void {
    if (this.identityForm.invalid) {
      // 触发所有表单验证
      Object.values(this.identityForm.controls).forEach(control => {
        if (control.invalid) {
          control.markAsDirty();
          control.updateValueAndValidity({ onlySelf: true });
        }
      });
      return;
    }

    this.submitting = true;
    const formData = this.identityForm.value;
    const requestParams: any = {
      serviceNumberId: this.serviceNumberId,
      ...formData
    };
    if (!this.isAdd && this.identityId) {
      requestParams.id = this.identityId;
    }

    this.http.postFormData(ConstantUtil.TENANT_SERVICENUMBER_IDENTITY_SAVE, requestParams, this.tempAvatarFile).subscribe(res => {
      if (res.success) {
        if (this.isAdd) {
          this.message.success(this.i18n.fanyi('servicenumber.identity.create-success'));
        } else {
          this.message.success(this.i18n.fanyi('servicenumber.identity.update-success'));
        }
        this.submitting = false;
        if (this.modalRef) {
          this.modalRef.close(true);
        }
      } else {
        if (this.isAdd) {
          this.message.error(`${this.i18n.fanyi('servicenumber.identity.create-failed')}: ${res.msg ?? ''}`);
        } else {
          this.message.error(`${this.i18n.fanyi('servicenumber.identity.update-failed')}: ${res.msg ?? ''}`);
        }
        this.submitting = false;
      }
    });
  }

  // 添加取消方法
  cancel(): void {
    if (this.modalRef) {
      this.modalRef.close();
    }
  }

  loadIdentityData(id: string): void {
    this.loading = true;
    this.http
      .post(`${ConstantUtil.TENANT_SERVICENUMBER_IDENTITY_DETAIL}/${id}`, {})
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe(res => {
        if (res.success) {
          this.identityForm.patchValue(res.data || {});
          if (res.data.avatarId) {
            this.loadAvatarImage(res.data.avatarId);
          }
          this.cdr.detectChanges();
        } else {
          this.message.error(this.i18n.fanyi('servicenumber.identity.load-failed'));
        }
      });
  }

  // 新增加载头像图片的方法
  loadAvatarImage(avatarId: string): void {
    this.imageService.loadImage(avatarId, 'M').subscribe(imageUrl => {
      if (imageUrl) {
        this.avatarUrl = imageUrl;

        // 从URL获取图片并转换为File对象
        fetch(imageUrl)
          .then(response => response.blob())
          .then(blob => {
            // 创建File对象
            this.tempAvatarFile = new File([blob], 'avatar.png', { type: blob.type ?? 'image/png' });
          })
          .catch(error => {
            console.error('get Avatar File Failed:', error);
          });

        this.cdr.detectChanges();
      } else {
        console.error('get Avatar Image Failed');
        this.avatarUrl = '';
        this.tempAvatarFile = null; // 确保在失败时清空tempAvatarFile
        this.cdr.detectChanges();
      }
    });
  }
  showAvatarUploadModal(): void {
    this.isAvatarModalVisible = true;
  }

  handleAvatarModalCancel(): void {
    this.isAvatarModalVisible = false;
  }

  handleAvatarCropped(event: { blob: Blob; width: number }): void {
    this.tempAvatarFile = new File([event.blob], 'avatar.png', { type: 'image/png' });
    // 创建本地预览URL
    const reader = new FileReader();
    reader.onload = (e: any) => {
      this.avatarUrl = e.target.result;
      this.identityForm.patchValue({
        avatarId: this.avatarUrl
      });
      this.handleAvatarModalCancel();
      this.cdr.detectChanges();
    };
    reader.readAsDataURL(this.tempAvatarFile);
  }
}
